import {
  BasketballGameReportQuarter,
  EventReportType,
  EventReportTypeEnum,
  EventReportVisibility,
  EventReportVisibilityEnum,
} from '@hefe/models';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

const FEATURE_KEY = 'eventReports';

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

interface GenerateWizardGeneralInfo {
  type: EventReportTypeEnum;
  writing_type: 'neutral' | 'home' | 'away';
  visibility: EventReportVisibilityEnum;
}

interface GenerateWizardQuartersPayload {
  score_home: number;
  score_guest: number;
  quarters: BasketballGameReportQuarter[];
}

interface GenerateWizardSummaryPayload {
  summary: string;
  top_performer_home: string;
  top_performer_guest: string;
}

interface GenerateWizardPregamePayload {
  additional_info: string;
}

export interface EventReportsState {
  generateWizard: {
    currentStep: number;
    data: {
      type: EventReportTypeEnum;
      writing_type: string;
      visibility: EventReportVisibilityEnum;
      score_home?: number;
      score_guest?: number;
      quarters: BasketballGameReportQuarter[];
      summary?: string;
      top_performer_home?: string;
      top_performer_guest?: string;
      additional_info?: string;
    };
  };
}

const initialState: EventReportsState = {
  generateWizard: {
    currentStep: 0,
    data: {
      quarters: [],
      visibility: EventReportVisibility.PRIVATE,
      type: EventReportType.POST,
      writing_type: 'neutral',
    },
  },
};

const eventReportsSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    setCurrentWizardStep: (state, action: PayloadAction<number>) => {
      state.generateWizard.currentStep = action.payload;
    },
    setGeneralInfo: (
      state,
      action: PayloadAction<GenerateWizardGeneralInfo>
    ) => {
      state.generateWizard.data.type = action.payload.type;
      state.generateWizard.data.writing_type = action.payload.writing_type;
      state.generateWizard.data.visibility = action.payload.visibility;
      state.generateWizard.currentStep = state.generateWizard.currentStep + 1;
    },
    setQuarters: (
      state,
      action: PayloadAction<GenerateWizardQuartersPayload>
    ) => {
      state.generateWizard.data.score_home = action.payload.score_home;
      state.generateWizard.data.score_guest = action.payload.score_guest;

      state.generateWizard.data.quarters = action.payload.quarters;

      state.generateWizard.currentStep = state.generateWizard.currentStep + 1;
    },
    setPregameData: (
      state,
      action: PayloadAction<GenerateWizardPregamePayload>
    ) => {
      state.generateWizard.data.additional_info =
        action.payload.additional_info;
      state.generateWizard.currentStep = state.generateWizard.currentStep + 1;
    },
    setSummary: (
      state,
      action: PayloadAction<GenerateWizardSummaryPayload>
    ) => {
      state.generateWizard.data.summary = action.payload.summary;
      state.generateWizard.data.top_performer_home =
        action.payload.top_performer_home;
      state.generateWizard.data.top_performer_guest =
        action.payload.top_performer_guest;

      state.generateWizard.currentStep = state.generateWizard.currentStep + 1;
    },
    resetWizard: (state) => {
      state.generateWizard = initialState.generateWizard;
    },
  },
});

/*
 * Export reducer for store configuration.
 */
const reducer = eventReportsSlice.reducer;
const actions = eventReportsSlice.actions;

const state = (rootState: any): EventReportsState => rootState[FEATURE_KEY];

const generateWizardSelector = createSelector(state, (state) => {
  return state.generateWizard;
});

export { actions, generateWizardSelector, reducer };
